import { useState, useMemo, Fragment, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  Chip,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Card,
  CardContent,
  Tooltip,
  Alert,
  useMediaQuery,
  useTheme,
  Tabs,
  Tab,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import FolderIcon from '@mui/icons-material/Folder';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PersonIcon from '@mui/icons-material/Person';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import SecurityIcon from '@mui/icons-material/Security';
import StorageIcon from '@mui/icons-material/Storage';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';

// Contexts and Components
import { useProject, TASK_STATUS } from '../../contexts/ProjectContext';
import { RESOURCE_TYPES } from '../../contexts/PermissionContext';
import { useNotification } from '../../contexts/NotificationContext';
import { useAuth } from '../../contexts/AuthContext';
import PermissionDialog from '../../components/Permissions/PermissionDialog';
import { StatCardSkeleton, PageHeaderCard } from '../../components/common';
import { usersAPI } from '../../services/api';

// Utils and Constants
import { getProjectStatusColor, getProjectStatusLabel, formatDate } from '../../utils/helper';
import { RESOURCE_TYPE_OPTIONS, RESOURCE_TYPE_VALUES, RESOURCE_TYPE_CONFIG } from '../../constants/resourceTypes';

// Calculate schedule status for a task
const getScheduleStatus = (task, projectStartDate) => {
  if (task.status === 'completed') {
    return { status: 'completed', label: 'Completed', color: 'success', icon: CheckCircleIcon };
  }
  if (task.status === 'not_started') {
    const today = new Date();
    const dueDate = new Date(task.dueDate);
    const daysUntilDue = Math.ceil((dueDate - today) / (1000 * 60 * 60 * 24));
    if (daysUntilDue < 0) {
      return { status: 'overdue', label: 'Overdue', color: 'error', icon: TrendingDownIcon, days: Math.abs(daysUntilDue) };
    }
    return { status: 'not_started', label: 'Not Started', color: 'default', icon: TrendingFlatIcon };
  }

  const today = new Date();
  const startDate = new Date(projectStartDate);
  const dueDate = new Date(task.dueDate);

  // Calculate expected progress based on time elapsed
  const totalDuration = dueDate - startDate;
  const elapsed = today - startDate;
  const expectedProgress = Math.min(100, Math.max(0, (elapsed / totalDuration) * 100));

  const progressDiff = task.progress - expectedProgress;
  const daysUntilDue = Math.ceil((dueDate - today) / (1000 * 60 * 60 * 24));

  if (daysUntilDue < 0) {
    return { status: 'overdue', label: `${Math.abs(daysUntilDue)}d overdue`, color: 'error', icon: TrendingDownIcon, days: Math.abs(daysUntilDue) };
  }

  if (progressDiff >= 15) {
    return { status: 'ahead', label: 'Ahead', color: 'success', icon: TrendingUpIcon, diff: Math.round(progressDiff) };
  } else if (progressDiff <= -15) {
    return { status: 'behind', label: 'Behind', color: 'error', icon: TrendingDownIcon, diff: Math.round(Math.abs(progressDiff)) };
  } else {
    return { status: 'on_track', label: 'On Track', color: 'primary', icon: TrendingFlatIcon };
  }
};

const ProjectDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, getActiveCompany, ROLES } = useAuth();
  const theme = useTheme();
  const { showError, showSuccess } = useNotification();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const {
    getProjectById,
    getProjectTasks,
    isProjectsLoading,
    fetchProjectDetails,
    addTask,
    updateTask,
    deleteTask,
    addSubtask,
    updateSubtask,
    deleteSubtask,
    getProjectResources,
    addResource,
    updateResource,
    deleteResource,
  } = useProject();

  const project = getProjectById(id);
  const tasks = getProjectTasks(id);
  const resources = getProjectResources(id);
  const activeCompany = useMemo(() => getActiveCompany(), [getActiveCompany]);
  const activeCompanyId = activeCompany?.id || null;
  const activeCompanyRole = (activeCompany?.role || '').toLowerCase();
  const canManagePermissions = [ROLES.COMPANY_ADMIN, ROLES.MANAGER].includes(activeCompanyRole);

  // Loading state
  const [isLoading, setIsLoading] = useState(true);
  const [isAccessDenied, setIsAccessDenied] = useState(false);
  const [companyUsers, setCompanyUsers] = useState([]);

  useEffect(() => {
    if (!id) {
      return;
    }

    let isMounted = true;

    const loadProjectDetails = async () => {
      setIsLoading(true);
      setIsAccessDenied(false);

      try {
        await fetchProjectDetails(id);
      } catch (error) {
        if (isMounted) {
          const accessDenied = error?.status === 403 || /access denied/i.test(error?.message || '');
          if (accessDenied) {
            setIsAccessDenied(true);
          }
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadProjectDetails();

    return () => {
      isMounted = false;
    };
  }, [id, fetchProjectDetails]);

  const loadCompanyUsers = useCallback(async () => {
    if (!activeCompanyId) {
      setCompanyUsers([]);
      return;
    }

    try {
      const response = await usersAPI.listByCompany(activeCompanyId, {
        includeSelf: true,
        page: 1,
        // pageSize: 200,
        sortBy: 'name',
        sortOrder: 'asc',
      });

      const items = Array.isArray(response?.items) ? response.items : [];
      const normalized = items
        .map((userItem) => ({
          id: userItem.id,
          name: userItem.name || userItem.email,
          email: userItem.email,
          role: userItem.role,
          roleTitle: userItem.roleTitle || userItem.role_title || '',
        }));

      setCompanyUsers(normalized);
    } catch (error) {
      setCompanyUsers([]);
      if (!error?.isNotified) {
        showError(error?.message || 'Failed to load company users');
      }
    }
  }, [activeCompanyId, showError]);

  useEffect(() => {
    loadCompanyUsers();
  }, [loadCompanyUsers]);

  const [expandedTasks, setExpandedTasks] = useState({});
  const [resourceDialogOpen, setResourceDialogOpen] = useState(false);
  const [selectedResource, setSelectedResource] = useState(null);
  const [resourceForm, setResourceForm] = useState({
    type: RESOURCE_TYPE_VALUES.SERVER,
    name: '',
    description: '',
    details: '',
  });
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [subtaskDialogOpen, setSubtaskDialogOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [taskForm, setTaskForm] = useState({
    name: '',
    description: '',
    status: 'not_started',
    assigneeId: '',
    assignee: '',
    dueDate: '',
  });
  const [subtaskForm, setSubtaskForm] = useState({
    name: '',
    status: 'not_started',
    assigneeId: '',
    assignee: '',
  });

  // Tab state
  const [activeTab, setActiveTab] = useState(0);

  // Permission dialog state
  const [permissionDialogOpen, setPermissionDialogOpen] = useState(false);
  const [permissionTarget, setPermissionTarget] = useState({
    type: null,
    id: null,
    name: '',
    parentInfo: null,
  });

  const roleLabelByUserId = useMemo(() => {
    return companyUsers.reduce((acc, member) => {
      const roleText = (member.role || '').replace(/_/g, ' ').trim();
      const fallbackRole = roleText ? roleText.charAt(0).toUpperCase() + roleText.slice(1) : 'Project member';
      acc[member.id] = member.roleTitle || fallbackRole;
      return acc;
    }, {});
  }, [companyUsers]);

  // Calculate statistics
  const stats = useMemo(() => {
    if (!tasks) return { total: 0, completed: 0, inProgress: 0 };
    const total = tasks.length;
    const completed = tasks.filter((t) => t.status === TASK_STATUS.COMPLETED).length;
    const inProgress = tasks.filter((t) => t.status === TASK_STATUS.IN_PROGRESS).length;
    return { total, completed, inProgress };
  }, [tasks]);

  const projectPermissionUsers = useMemo(() => {
    if (!project) {
      return [];
    }

    const userMap = new Map();

    if (project.managerId) {
      userMap.set(project.managerId, {
        id: project.managerId,
        name: project.manager || 'Project Manager',
        subtitle: roleLabelByUserId[project.managerId] || 'Project manager',
      });
    }

    (tasks || []).forEach((task) => {
      if (task.assigneeId) {
        userMap.set(task.assigneeId, {
          id: task.assigneeId,
          name: task.assignee || `User ${task.assigneeId}`,
          subtitle: roleLabelByUserId[task.assigneeId] || 'Project member',
        });
      }

      (task.subtasks || []).forEach((subtask) => {
        if (subtask.assigneeId) {
          userMap.set(subtask.assigneeId, {
            id: subtask.assigneeId,
            name: subtask.assignee || `User ${subtask.assigneeId}`,
            subtitle: roleLabelByUserId[subtask.assigneeId] || 'Project member',
          });
        }
      });
    });

    return Array.from(userMap.values());
  }, [project, tasks, roleLabelByUserId]);

  const permissionAssignees = useMemo(() => {
    const currentUserId = user?.id ? String(user.id) : null;
    if (!currentUserId) {
      return projectPermissionUsers;
    }

    return projectPermissionUsers.filter((assignee) => String(assignee.id) !== currentUserId);
  }, [projectPermissionUsers, user?.id]);

  if (isAccessDenied) {
    return (
      <Box>
        <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate('/projects')} sx={{ mb: 2 }}>
          Back to Projects
        </Button>
        <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
          <SecurityIcon sx={{ fontSize: 64, color: 'warning.main', mb: 2 }} />
          <Typography variant="h6" color="text.primary" gutterBottom>
            Access Denied
          </Typography>
          <Typography variant="body2" color="text.secondary">
            You don&apos;t have access to this project.
          </Typography>
        </Paper>
      </Box>
    );
  }

  if (!project) {
    if (isProjectsLoading || isLoading) {
      return (
        <Box>
          <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate('/projects')} sx={{ mb: 2 }}>
            Back to Projects
          </Button>
          <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
            <FolderIcon sx={{ fontSize: 64, color: 'grey.300', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Loading Project...
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Please wait while project details are being fetched.
            </Typography>
          </Paper>
        </Box>
      );
    }

    return (
      <Box>
        <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate('/projects')} sx={{ mb: 2 }}>
          Back to Projects
        </Button>
        <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
          <FolderIcon sx={{ fontSize: 64, color: 'grey.300', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Project Not Found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            The project you are looking for does not exist or has been removed.
          </Typography>
        </Paper>
      </Box>
    );
  }

  const handleBack = () => {
    navigate('/projects');
  };

  const toggleTaskExpand = (taskId) => {
    setExpandedTasks((prev) => ({
      ...prev,
      [taskId]: !prev[taskId],
    }));
  };

  const handleOpenTaskDialog = (task = null) => {
    if (task) {
      setTaskForm({
        name: task.name,
        description: task.description,
        status: task.status,
        assigneeId: task.assigneeId,
        assignee: task.assignee,
        dueDate: task.dueDate,
      });
      setSelectedTask(task);
    } else {
      const projectOwner = companyUsers.find((u) => u.id === project.managerId);
      // Calculate default due date (3 weeks from now)
      const defaultDueDate = new Date();
      defaultDueDate.setDate(defaultDueDate.getDate() + 21);
      const formattedDueDate = defaultDueDate.toISOString().split('T')[0];

      setTaskForm({
        name: '',
        description: '',
        status: 'not_started',
        assigneeId: projectOwner?.id || '',
        assignee: projectOwner?.name || '',
        dueDate: formattedDueDate,
      });
      setSelectedTask(null);
    }
    setTaskDialogOpen(true);
  };

  const handleCloseTaskDialog = () => {
    setTaskDialogOpen(false);
    setSelectedTask(null);
  };

  const handleSaveTask = async () => {
    const taskData = {
      ...taskForm,
      assignee: companyUsers.find((u) => u.id === taskForm.assigneeId)?.name || '',
    };

    try {
      if (selectedTask) {
        await updateTask(id, selectedTask.id, taskData);
        showSuccess('Task updated successfully');
      } else {
        await addTask(id, taskData);
        showSuccess('Task added successfully');
      }
      handleCloseTaskDialog();
    } catch (error) {
      if (!error?.isNotified) {
        showError(error?.message || 'Failed to save task');
      }
    }
  };

  const handleDeleteTask = async (taskId) => {
    try {
      await deleteTask(id, taskId);
      showSuccess('Task removed successfully');
    } catch (error) {
      if (!error?.isNotified) {
        showError(error?.message || 'Failed to delete task');
      }
    }
  };

  const handleOpenSubtaskDialog = (task) => {
    setSelectedTask(task);
    // Prefill owner with parent task's owner
    setSubtaskForm({
      name: '',
      status: 'not_started',
      assigneeId: task.assigneeId || '',
      assignee: task.assignee || '',
    });
    setSubtaskDialogOpen(true);
  };

  const handleCloseSubtaskDialog = () => {
    setSubtaskDialogOpen(false);
    setSelectedTask(null);
  };

  const handleSaveSubtask = async () => {
    const subtaskData = {
      ...subtaskForm,
      assignee: companyUsers.find((u) => u.id === subtaskForm.assigneeId)?.name || '',
    };
    try {
      await addSubtask(id, selectedTask.id, subtaskData);
      showSuccess('Subtask added successfully');
      handleCloseSubtaskDialog();
    } catch (error) {
      if (!error?.isNotified) {
        showError(error?.message || 'Failed to save subtask');
      }
    }
  };

  const handleDeleteSubtask = async (taskId, subtaskId) => {
    try {
      await deleteSubtask(id, taskId, subtaskId);
      showSuccess('Subtask removed successfully');
    } catch (error) {
      if (!error?.isNotified) {
        showError(error?.message || 'Failed to delete subtask');
      }
    }
  };

  const handleUpdateSubtaskStatus = async (taskId, subtaskId, newStatus) => {
    try {
      await updateSubtask(id, taskId, subtaskId, { status: newStatus });
      showSuccess('Subtask updated successfully');
    } catch (error) {
      if (!error?.isNotified) {
        showError(error?.message || 'Failed to update subtask status');
      }
    }
  };

  // Permission dialog handlers
  const handleOpenProjectPermissions = () => {
    if (!canManagePermissions) return;
    setPermissionTarget({
      type: RESOURCE_TYPES.PROJECT,
      id: project.id,
      name: project.name,
      parentInfo: null,
    });
    setPermissionDialogOpen(true);
  };

  const handleOpenTaskPermissions = (task) => {
    if (!canManagePermissions) return;
    setPermissionTarget({
      type: RESOURCE_TYPES.TASK,
      id: task.id,
      name: task.name,
      parentInfo: { type: RESOURCE_TYPES.PROJECT, id: project.id, name: project.name },
    });
    setPermissionDialogOpen(true);
  };

  const handleOpenSubtaskPermissions = (task, subtask) => {
    if (!canManagePermissions) return;
    setPermissionTarget({
      type: RESOURCE_TYPES.SUBTASK,
      id: subtask.id,
      name: subtask.name,
      parentInfo: {
        type: RESOURCE_TYPES.TASK,
        id: task.id,
        name: task.name,
        parentInfo: { type: RESOURCE_TYPES.PROJECT, id: project.id, name: project.name },
      },
    });
    setPermissionDialogOpen(true);
  };

  const handleClosePermissionDialog = () => {
    setPermissionDialogOpen(false);
    setPermissionTarget({ type: null, id: null, name: '', parentInfo: null });
  };

  // Resource dialog handlers
  const handleOpenResourceDialog = (resource = null) => {
    if (resource) {
      setResourceForm({
        type: resource.type,
        name: resource.name,
        description: resource.description || '',
        details: resource.details || '',
      });
      setSelectedResource(resource);
    } else {
      setResourceForm({
        type: RESOURCE_TYPE_VALUES.SERVER,
        name: '',
        description: '',
        details: '',
      });
      setSelectedResource(null);
    }
    setResourceDialogOpen(true);
  };

  const handleCloseResourceDialog = () => {
    setResourceDialogOpen(false);
    setSelectedResource(null);
  };

  const handleSaveResource = async () => {
    try {
      if (selectedResource) {
        await updateResource(id, selectedResource.id, resourceForm);
        showSuccess('Resource updated successfully');
      } else {
        await addResource(id, resourceForm);
        showSuccess('Resource added successfully');
      }
      handleCloseResourceDialog();
    } catch (error) {
      if (!error?.isNotified) {
        showError(error?.message || 'Failed to save resource');
      }
    }
  };

  const handleDeleteResource = async (resourceId) => {
    try {
      await deleteResource(id, resourceId);
      showSuccess('Resource removed successfully');
    } catch (error) {
      if (!error?.isNotified) {
        showError(error?.message || 'Failed to delete resource');
      }
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ mb: 2 }}
          color="inherit"
        >
          Back to Projects
        </Button>
        <PageHeaderCard
          mb={0}
          title={project.name}
          subtitle={project.description}
          titleVariant={isMobile ? 'h5' : 'h4'}
          icon={!isMobile ? <FolderIcon sx={{ color: 'primary.main', fontSize: 28 }} /> : null}
          titleSuffix={(
            <Chip
              label={getProjectStatusLabel(project.status)}
              color={getProjectStatusColor(project.status)}
              size="small"
            />
          )}
          actions={(
            <Box sx={{
              display: 'flex',
              gap: 1,
              flexDirection: { xs: 'column', sm: 'row' },
              width: { xs: '100%', sm: 'auto' }
            }}>
              {canManagePermissions && (
                <Button
                  variant="outlined"
                  startIcon={<SecurityIcon />}
                  onClick={handleOpenProjectPermissions}
                  fullWidth={isMobile}
                  size={isMobile ? 'small' : 'medium'}
                >
                  Permissions
                </Button>
              )}
            </Box>
          )}
        />
      </Box>

      {/* Project Info Card */}
      <Card
        elevation={0}
        sx={{
          mb: 3,
          borderRadius: 3,
          backgroundColor: 'background.paper',
          boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
          border: '1px solid',
          borderColor: 'grey.100',
        }}
      >
        <Box sx={{ p: { xs: 2, sm: 3 }, pb: { xs: 1.5, sm: 2 }, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            width: 8,
            height: 24,
            borderRadius: 1,
            backgroundColor: 'primary.main',
          }} />
          <Typography variant="h6" fontWeight={600} sx={{ fontSize: { xs: '1rem', sm: '1.1rem' } }}>
            Project Details
          </Typography>
        </Box>
        <Divider />
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Grid container spacing={3}>
            <Grid size={{ xs: 6, sm: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{ p: 1, borderRadius: 1.5, backgroundColor: '#1976d212' }}>
                  <PersonIcon sx={{ color: '#1976d2', fontSize: 20 }} />
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                    Manager
                  </Typography>
                  <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.85rem' }}>
                    {project.manager}
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{ p: 1, borderRadius: 1.5, backgroundColor: '#2e7d3212' }}>
                  <CalendarMonthIcon sx={{ color: '#2e7d32', fontSize: 20 }} />
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                    Start Date
                  </Typography>
                  <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.85rem' }}>
                    {formatDate(project.startDate)}
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{ p: 1, borderRadius: 1.5, backgroundColor: '#ed6c0212' }}>
                  <CalendarMonthIcon sx={{ color: '#ed6c02', fontSize: 20 }} />
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                    Due Date
                  </Typography>
                  <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.85rem' }}>
                    {formatDate(project.dueDate)}
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{ p: 1, borderRadius: 1.5, backgroundColor: '#9c27b012' }}>
                  <TrendingUpIcon sx={{ color: '#9c27b0', fontSize: 20 }} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                    Progress
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LinearProgress
                      variant="determinate"
                      value={project.progress}
                      sx={{
                        flex: 1,
                        minWidth: { xs: 60, sm: 80 },
                        height: 6,
                        borderRadius: 3,
                        bgcolor: 'grey.200',
                        '& .MuiLinearProgress-bar': {
                          borderRadius: 3,
                          bgcolor: project.progress === 100 ? 'success.main' : 'primary.main',
                        },
                      }}
                    />
                    <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
                      {project.progress}%
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Statistics Cards */}
      {isLoading ? (
        <StatCardSkeleton count={3} />
      ) : (
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(3, 1fr)' },
          gap: { xs: 1.5, sm: 2 },
          mb: 3
        }}>
          {[
            { title: 'Total Tasks', value: stats.total, icon: AssignmentIcon, color: '#1976d2' },
            { title: 'Completed', value: stats.completed, icon: CheckCircleIcon, color: '#2e7d32' },
            { title: 'In Progress', value: stats.inProgress, icon: AssignmentIcon, color: '#0288d1' },
          ].map((item) => {
            const IconComponent = item.icon;
            return (
              <Card
                key={item.title}
                elevation={0}
                sx={{
                  border: '1px solid',
                  borderColor: 'grey.200',
                  borderRadius: 2,
                  transition: 'all 0.2s ease-in-out',
                  cursor: 'pointer',
                  '&:hover': {
                    borderColor: 'primary.main',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
                  },
                }}
              >
                <CardContent sx={{ p: { xs: 1.5, sm: 2.5 }, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem' }, mb: 0.5 }}>
                      {item.title}
                    </Typography>
                    <Typography variant="h4" fontWeight={700} sx={{ fontSize: { xs: '1.5rem', sm: '2rem' }, lineHeight: 1.2 }}>
                      {item.value}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      p: { xs: 1, sm: 1.5 },
                      borderRadius: 2,
                      backgroundColor: `${item.color}12`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <IconComponent sx={{ color: item.color, fontSize: { xs: 24, sm: 28 } }} />
                  </Box>
                </CardContent>
              </Card>
            );
          })}
        </Box>
      )}

      {/* Tabs for Tasks & Resources */}
      <Card
        elevation={0}
        sx={{
          borderRadius: 3,
          backgroundColor: 'background.paper',
          boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
          border: '1px solid',
          borderColor: 'grey.100',
        }}
      >
        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: { xs: 1, sm: 2 } }}>
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => setActiveTab(newValue)}
            sx={{
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 600,
                fontSize: { xs: '0.85rem', sm: '0.95rem' },
                minHeight: 56,
              },
            }}
          >
            <Tab
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AssignmentIcon fontSize="small" />
                  Tasks
                  <Chip label={stats.total} size="small" sx={{ height: 20, fontSize: '0.75rem' }} />
                </Box>
              }
            />
            <Tab
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <StorageIcon fontSize="small" />
                  Resources
                  <Chip label={resources.length} size="small" sx={{ height: 20, fontSize: '0.75rem' }} />
                </Box>
              }
            />
          </Tabs>
        </Box>

        {/* Tasks Tab */}
        {activeTab === 0 && (
          <>
            <Box sx={{ p: { xs: 2, sm: 2 }, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                size={isMobile ? 'small' : 'medium'}
                startIcon={<AddIcon />}
                onClick={() => handleOpenTaskDialog()}
              >
                Add Task
              </Button>
            </Box>

            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size={isMobile ? 'small' : 'medium'}>
                <TableHead>
                  <TableRow>
                    <TableCell width={40} sx={{ borderBottom: '2px solid', borderColor: 'grey.200' }} />
                    <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.8rem', borderBottom: '2px solid', borderColor: 'grey.200', minWidth: 120 }}>Task Name</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.8rem', borderBottom: '2px solid', borderColor: 'grey.200' }}>Status</TableCell>
                    {!isMobile && <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.8rem', borderBottom: '2px solid', borderColor: 'grey.200', minWidth: 120 }}>Progress</TableCell>}
                    {!isMobile && <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.8rem', borderBottom: '2px solid', borderColor: 'grey.200' }}>Schedule</TableCell>}
                    {!isTablet && <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.8rem', borderBottom: '2px solid', borderColor: 'grey.200' }}>Owner</TableCell>}
                    {!isMobile && <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.8rem', borderBottom: '2px solid', borderColor: 'grey.200' }}>Due Date</TableCell>}
                    <TableCell align="center" sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.8rem', borderBottom: '2px solid', borderColor: 'grey.200' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tasks.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={isMobile ? 4 : isTablet ? 6 : 8} align="center" sx={{ py: 6 }}>
                        <AssignmentIcon sx={{ fontSize: 48, color: 'grey.300', mb: 1 }} />
                        <Typography color="text.secondary">No tasks yet</Typography>
                        <Button
                          variant="text"
                          size={isMobile ? 'small' : 'medium'}
                          startIcon={<AddIcon />}
                          onClick={() => handleOpenTaskDialog()}
                          sx={{ mt: 1 }}
                        >
                          Add first task
                        </Button>
                      </TableCell>
                    </TableRow>
                  ) : (
                    tasks.map((task, index) => (
                      <Fragment key={task.id}>
                        <TableRow
                          sx={{
                            backgroundColor: index % 2 === 0 ? 'transparent' : 'grey.50',
                            cursor: 'pointer',
                            transition: 'background-color 0.15s ease',
                            '&:hover': {
                              backgroundColor: 'primary.50',
                            },
                          }}
                        >
                          <TableCell sx={{ borderBottom: '1px solid', borderColor: 'grey.100' }}>
                            {task.subtasks && task.subtasks.length > 0 && (
                              <IconButton
                                size="small"
                                onClick={() => toggleTaskExpand(task.id)}
                              >
                                {expandedTasks[task.id] ? (
                                  <ExpandLessIcon />
                                ) : (
                                  <ExpandMoreIcon />
                                )}
                              </IconButton>
                            )}
                          </TableCell>
                          <TableCell sx={{ borderBottom: '1px solid', borderColor: 'grey.100' }}>
                            <Box>
                              <Typography fontWeight={500} sx={{ fontSize: '0.9rem' }}>{task.name}</Typography>
                              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                                {task.description}
                              </Typography>
                              {task.subtasks && task.subtasks.length > 0 && (
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                  {task.subtasks.length} subtask{task.subtasks.length > 1 ? 's' : ''}
                                </Typography>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell sx={{ borderBottom: '1px solid', borderColor: 'grey.100' }}>
                            <Chip
                              label={getProjectStatusLabel(task.status)}
                              color={getProjectStatusColor(task.status)}
                              size="small"
                              sx={{
                                fontWeight: 500,
                                fontSize: '0.7rem',
                                height: 24,
                                '& .MuiChip-label': { px: 1.5 }
                              }}
                            />
                          </TableCell>
                          {!isMobile && (
                            <TableCell sx={{ borderBottom: '1px solid', borderColor: 'grey.100' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <LinearProgress
                                  variant="determinate"
                                  value={task.progress}
                                  sx={{
                                    flex: 1,
                                    height: 6,
                                    borderRadius: 3,
                                    bgcolor: 'grey.200',
                                    '& .MuiLinearProgress-bar': {
                                      borderRadius: 3,
                                      bgcolor: task.progress === 100 ? 'success.main' : 'primary.main',
                                    },
                                  }}
                                />
                                <Typography variant="caption" fontWeight={500} color="text.secondary" sx={{ minWidth: 35, fontSize: '0.75rem' }}>
                                  {task.progress}%
                                </Typography>
                              </Box>
                            </TableCell>
                          )}
                          {!isMobile && (() => {
                            const schedule = getScheduleStatus(task, project.startDate);
                            const ScheduleIcon = schedule.icon;
                            return (
                              <TableCell sx={{ borderBottom: '1px solid', borderColor: 'grey.100' }}>
                                <Tooltip
                                  title={
                                    schedule.status === 'ahead' ? `${schedule.diff}% ahead of expected progress` :
                                      schedule.status === 'behind' ? `${schedule.diff}% behind expected progress` :
                                        schedule.status === 'overdue' ? `${schedule.days} days past due date` :
                                          schedule.label
                                  }
                                  arrow
                                >
                                  <Chip
                                    icon={<ScheduleIcon sx={{ fontSize: 14 }} />}
                                    label={schedule.label}
                                    size="small"
                                    color={schedule.color}
                                    variant={schedule.status === 'on_track' ? 'outlined' : 'filled'}
                                    sx={{
                                      fontWeight: 500,
                                      fontSize: '0.7rem',
                                      height: 24,
                                      '& .MuiChip-label': { px: 1 },
                                      '& .MuiChip-icon': { ml: 0.5 },
                                    }}
                                  />
                                </Tooltip>
                              </TableCell>
                            );
                          })()}
                          {!isTablet && (
                            <TableCell sx={{ borderBottom: '1px solid', borderColor: 'grey.100' }}>
                              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                                {task.assignee}
                              </Typography>
                            </TableCell>
                          )}
                          {!isMobile && (
                            <TableCell sx={{ borderBottom: '1px solid', borderColor: 'grey.100' }}>
                              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                                {formatDate(task.dueDate)}
                              </Typography>
                            </TableCell>
                          )}
                          <TableCell align="center" sx={{ borderBottom: '1px solid', borderColor: 'grey.100' }}>
                            {canManagePermissions && (
                              <Tooltip title="Permissions" arrow>
                                <IconButton
                                  size="small"
                                  onClick={() => handleOpenTaskPermissions(task)}
                                  sx={{ color: 'warning.main' }}
                                >
                                  <SecurityIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            <Tooltip title="Add Subtask" arrow>
                              <IconButton
                                size="small"
                                onClick={() => handleOpenSubtaskDialog(task)}
                                sx={{ color: 'success.main' }}
                              >
                                <AddIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Edit" arrow>
                              <IconButton
                                size="small"
                                onClick={() => handleOpenTaskDialog(task)}
                                sx={{ color: 'primary.main' }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete" arrow>
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteTask(task.id)}
                                sx={{ color: 'error.main' }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                        {task.subtasks && task.subtasks.length > 0 && (
                          <TableRow>
                            <TableCell colSpan={isMobile ? 4 : isTablet ? 6 : 8} sx={{ py: 0, bgcolor: 'grey.50' }}>
                              <Collapse in={expandedTasks[task.id]} timeout="auto" unmountOnExit>
                                <Box sx={{ py: 2, px: { xs: 2, sm: 6 } }}>
                                  <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
                                    Subtasks ({task.subtasks.length})
                                  </Typography>
                                  <Table size="small">
                                    <TableHead>
                                      <TableRow>
                                        <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Owner</TableCell>
                                        <TableCell align="center" sx={{ fontWeight: 600 }}>Actions</TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {task.subtasks.map((subtask) => (
                                        <TableRow key={subtask.id}>
                                          <TableCell>{subtask.name}</TableCell>
                                          <TableCell>
                                            <FormControl size="small" sx={{ minWidth: 130 }}>
                                              <Select
                                                value={subtask.status}
                                                onChange={(e) => handleUpdateSubtaskStatus(task.id, subtask.id, e.target.value)}
                                                sx={{ '& .MuiSelect-select': { py: 0.5 } }}
                                              >
                                                <MenuItem value="not_started">Not Started</MenuItem>
                                                <MenuItem value="in_progress">In Progress</MenuItem>
                                                <MenuItem value="on_hold">On Hold</MenuItem>
                                                <MenuItem value="completed">Completed</MenuItem>
                                              </Select>
                                            </FormControl>
                                          </TableCell>
                                          <TableCell>{subtask.assignee}</TableCell>
                                          <TableCell align="center">
                                            {canManagePermissions && (
                                              <Tooltip title="Permissions" arrow>
                                                <IconButton
                                                  size="small"
                                                  onClick={() => handleOpenSubtaskPermissions(task, subtask)}
                                                  sx={{ color: 'warning.main' }}
                                                >
                                                  <SecurityIcon fontSize="small" />
                                                </IconButton>
                                              </Tooltip>
                                            )}
                                            <Tooltip title="Delete" arrow>
                                              <IconButton
                                                size="small"
                                                onClick={() => handleDeleteSubtask(task.id, subtask.id)}
                                                sx={{ color: 'error.main' }}
                                              >
                                                <DeleteIcon fontSize="small" />
                                              </IconButton>
                                            </Tooltip>
                                          </TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        )}
                      </Fragment>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Info Alert */}
            <Alert severity="info" sx={{ m: 2, mt: 0, borderRadius: 2 }}>
              Task status changes are automatically synced to the weekly report. Progress is calculated based on subtask completion.
            </Alert>
          </>
        )}

        {/* Resources Tab */}
        {activeTab === 1 && (
          <>
            <Box sx={{ p: { xs: 2, sm: 2 }, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                size={isMobile ? 'small' : 'medium'}
                startIcon={<AddIcon />}
                onClick={() => handleOpenResourceDialog()}
                sx={{ bgcolor: '#7b1fa2', '&:hover': { bgcolor: '#6a1b9a' } }}
              >
                Add Resource
              </Button>
            </Box>

            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size={isMobile ? 'small' : 'medium'}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.8rem', borderBottom: '2px solid', borderColor: 'grey.200' }}>Type</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.8rem', borderBottom: '2px solid', borderColor: 'grey.200', minWidth: 120 }}>Name</TableCell>
                    {!isMobile && <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.8rem', borderBottom: '2px solid', borderColor: 'grey.200' }}>Description</TableCell>}
                    {!isTablet && <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.8rem', borderBottom: '2px solid', borderColor: 'grey.200' }}>Details</TableCell>}
                    <TableCell align="center" sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.8rem', borderBottom: '2px solid', borderColor: 'grey.200' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {resources.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={isMobile ? 3 : isTablet ? 4 : 5} align="center" sx={{ py: 6 }}>
                        <StorageIcon sx={{ fontSize: 48, color: 'grey.300', mb: 1 }} />
                        <Typography color="text.secondary">No resources yet</Typography>
                        <Button
                          variant="text"
                          size={isMobile ? 'small' : 'medium'}
                          startIcon={<AddIcon />}
                          onClick={() => handleOpenResourceDialog()}
                          sx={{ mt: 1 }}
                        >
                          Add first resource
                        </Button>
                      </TableCell>
                    </TableRow>
                  ) : (
                    resources.map((resource, index) => {
                      const typeConfig = RESOURCE_TYPE_CONFIG[resource.type] || RESOURCE_TYPE_CONFIG[RESOURCE_TYPE_VALUES.SERVER];
                      const TypeIcon = typeConfig.icon;
                      return (
                        <TableRow
                          key={resource.id}
                          sx={{
                            backgroundColor: index % 2 === 0 ? 'transparent' : 'grey.50',
                            transition: 'background-color 0.15s ease',
                            '&:hover': {
                              backgroundColor: 'primary.50',
                            },
                          }}
                        >
                          <TableCell sx={{ borderBottom: '1px solid', borderColor: 'grey.100' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box
                                sx={{
                                  p: 0.75,
                                  borderRadius: 1,
                                  backgroundColor: `${typeConfig.color}15`,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                <TypeIcon sx={{ color: typeConfig.color, fontSize: 18 }} />
                              </Box>
                              <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.85rem' }}>
                                {typeConfig.label}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ borderBottom: '1px solid', borderColor: 'grey.100' }}>
                            <Typography fontWeight={500} sx={{ fontSize: '0.9rem' }}>{resource.name}</Typography>
                          </TableCell>
                          {!isMobile && (
                            <TableCell sx={{ borderBottom: '1px solid', borderColor: 'grey.100' }}>
                              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                                {resource.description || '-'}
                              </Typography>
                            </TableCell>
                          )}
                          {!isTablet && (
                            <TableCell sx={{ borderBottom: '1px solid', borderColor: 'grey.100' }}>
                              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem', fontFamily: 'monospace' }}>
                                {resource.details || '-'}
                              </Typography>
                            </TableCell>
                          )}
                          <TableCell align="center" sx={{ borderBottom: '1px solid', borderColor: 'grey.100' }}>
                            {(() => {
                              const canModifyResource = resource.createdById === user?.id;
                              return (
                                <>
                                  <Tooltip title="Edit" arrow>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleOpenResourceDialog(resource)}
                                      sx={{ color: 'primary.main' }}
                                      disabled={!canModifyResource}
                                    >
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Delete" arrow>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleDeleteResource(resource.id)}
                                      sx={{ color: 'error.main' }}
                                      disabled={!canModifyResource}
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </>
                              );
                            })()}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Resource Info Alert */}
            <Alert severity="info" sx={{ m: 2, mt: 0, borderRadius: 2 }}>
              Resources track servers, databases, APIs, and other infrastructure used by this project.
            </Alert>
          </>
        )}
      </Card>

      {/* Task Dialog */}
      <Dialog open={taskDialogOpen} onClose={handleCloseTaskDialog} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle sx={{ bgcolor: 'primary.main', color: 'secondary.contrastText' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AssignmentIcon sx={{ color: 'inherit' }} />
            <Typography variant="h6" color="inherit">{selectedTask ? 'Edit Task' : 'Add Task'}</Typography>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TextField
              fullWidth
              label="Task Name"
              value={taskForm.name}
              onChange={(e) => setTaskForm({ ...taskForm, name: e.target.value })}
              required
            />
            <TextField
              fullWidth
              label="Description"
              value={taskForm.description}
              onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })}
              multiline
              rows={3}
            />
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={taskForm.status}
                label="Status"
                onChange={(e) => setTaskForm({ ...taskForm, status: e.target.value })}
              >
                <MenuItem value="not_started">Not Started</MenuItem>
                <MenuItem value="in_progress">In Progress</MenuItem>
                <MenuItem value="on_hold">On Hold</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Owner</InputLabel>
              <Select
                value={taskForm.assigneeId}
                label="Owner"
                onChange={(e) => setTaskForm({ ...taskForm, assigneeId: e.target.value })}
              >
                <MenuItem disabled sx={{ opacity: 0.7, fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', color: 'text.secondary' }}>
                  <PersonIcon sx={{ fontSize: 16, mr: 1 }} /> Users
                </MenuItem>
                {companyUsers.map((user) => (
                  <MenuItem key={user.id} value={user.id} sx={{ pl: 4 }}>
                    {user.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Due Date"
                value={taskForm.dueDate ? dayjs(taskForm.dueDate) : null}
                format="DD MMM YYYY"
                onChange={(value) => {
                  setTaskForm({
                    ...taskForm,
                    dueDate: value ? value.format('YYYY-MM-DD') : '',
                  });
                }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                  },
                }}
              />
            </LocalizationProvider>
          </Box>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseTaskDialog} variant="outlined" color="inherit">Cancel</Button>
          <Button onClick={handleSaveTask} variant="contained" disabled={!taskForm.name}>
            {selectedTask ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Subtask Dialog */}
      <Dialog open={subtaskDialogOpen} onClose={handleCloseSubtaskDialog} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle sx={{ pb: 1, bgcolor: 'primary.main', color: 'secondary.contrastText' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AddIcon sx={{ color: 'inherit' }} />
            <Typography variant="h6" color="inherit">Add Subtask</Typography>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Parent Task
              </Typography>
              <Typography fontWeight={500}>{selectedTask?.name}</Typography>
            </Box>
            <TextField
              fullWidth
              label="Subtask Name"
              value={subtaskForm.name}
              onChange={(e) => setSubtaskForm({ ...subtaskForm, name: e.target.value })}
              required
            />
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={subtaskForm.status}
                label="Status"
                onChange={(e) => setSubtaskForm({ ...subtaskForm, status: e.target.value })}
              >
                <MenuItem value="not_started">Not Started</MenuItem>
                <MenuItem value="in_progress">In Progress</MenuItem>
                <MenuItem value="on_hold">On Hold</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Owner</InputLabel>
              <Select
                value={subtaskForm.assigneeId}
                label="Owner"
                onChange={(e) => setSubtaskForm({ ...subtaskForm, assigneeId: e.target.value })}
              >
                <MenuItem disabled sx={{ opacity: 0.7, fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', color: 'text.secondary' }}>
                  <PersonIcon sx={{ fontSize: 16, mr: 1 }} /> Users
                </MenuItem>
                {companyUsers.map((user) => (
                  <MenuItem key={user.id} value={user.id} sx={{ pl: 4 }}>
                    {user.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseSubtaskDialog} variant="outlined" color="inherit">Cancel</Button>
          <Button onClick={handleSaveSubtask} variant="contained" disabled={!subtaskForm.name}>
            Add
          </Button>
        </DialogActions>
      </Dialog>

      {/* Permission Dialog */}
      <PermissionDialog
        open={permissionDialogOpen}
        onClose={handleClosePermissionDialog}
        resourceType={permissionTarget.type}
        resourceId={permissionTarget.id}
        resourceName={permissionTarget.name}
        parentInfo={permissionTarget.parentInfo}
        companyId={activeCompanyId}
        assignees={permissionAssignees}
      />

      {/* Resource Dialog */}
      <Dialog open={resourceDialogOpen} onClose={handleCloseResourceDialog} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle sx={{ pb: 1, bgcolor: 'primary.main', color: 'secondary.contrastText' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <StorageIcon sx={{ color: 'inherit' }} />
            <Typography variant="h6" color="inherit">{selectedResource ? 'Edit Resource' : 'Add Resource'}</Typography>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
            <FormControl fullWidth>
              <InputLabel>Type</InputLabel>
              <Select
                value={resourceForm.type}
                label="Type"
                onChange={(e) => setResourceForm({ ...resourceForm, type: e.target.value })}
              >
                {RESOURCE_TYPE_OPTIONS.map((option) => {
                  const config = RESOURCE_TYPE_CONFIG[option.value] || RESOURCE_TYPE_CONFIG[RESOURCE_TYPE_VALUES.SERVER];
                  const TypeIcon = config.icon;
                  return (
                    <MenuItem key={option.value} value={option.value}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TypeIcon sx={{ color: config.color, fontSize: 18 }} />
                        {option.label}
                      </Box>
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Name"
              value={resourceForm.name}
              onChange={(e) => setResourceForm({ ...resourceForm, name: e.target.value })}
              required
              placeholder={
                resourceForm.type === RESOURCE_TYPE_VALUES.SERVER ? 'e.g., PROD-WEB-01' :
                  resourceForm.type === RESOURCE_TYPE_VALUES.DATABASE ? 'e.g., PostgreSQL Main' :
                    resourceForm.type === RESOURCE_TYPE_VALUES.FIREWALL ? 'e.g., Port 443' :
                      resourceForm.type === RESOURCE_TYPE_VALUES.CDN ? 'e.g., CloudFlare' :
                        resourceForm.type === RESOURCE_TYPE_VALUES.API ? 'e.g., Stripe API' :
                          'e.g., Service Name'
              }
            />
            <TextField
              fullWidth
              label="Description"
              value={resourceForm.description}
              onChange={(e) => setResourceForm({ ...resourceForm, description: e.target.value })}
              placeholder="Brief description of this resource"
            />
            <TextField
              fullWidth
              label="Details"
              value={resourceForm.details}
              onChange={(e) => setResourceForm({ ...resourceForm, details: e.target.value })}
              placeholder={
                resourceForm.type === RESOURCE_TYPE_VALUES.SERVER ? 'e.g., 192.168.1.100' :
                  resourceForm.type === RESOURCE_TYPE_VALUES.DATABASE ? 'e.g., db.internal:5432' :
                    resourceForm.type === RESOURCE_TYPE_VALUES.FIREWALL ? 'e.g., Inbound TCP 443' :
                      resourceForm.type === RESOURCE_TYPE_VALUES.CDN ? 'e.g., Zone: website.com' :
                        resourceForm.type === RESOURCE_TYPE_VALUES.API ? 'e.g., api.stripe.com' :
                          'Additional technical details'
              }
              helperText="Technical details like IP address, port, URL, etc."
            />
          </Box>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseResourceDialog} variant="outlined" color="inherit">Cancel</Button>
          <Button
            onClick={handleSaveResource}
            variant="contained"
            disabled={!resourceForm.name}
            sx={{ bgcolor: '#7b1fa2', '&:hover': { bgcolor: '#6a1b9a' } }}
          >
            {selectedResource ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProjectDetail;
